import { AssessmentInfo, Lms, VerityOptions } from "@verity/api-client/types";
import { VerityInstituteSettings } from "@verity/components";
import apiClient from "../../../helpers/api-client";
import i18n from "../../../i18n";

export type QuizWithProctoringOptions = Awaited<ReturnType<typeof apiClient.assessment.getCourseAssessments>>[number];

type CourseInfo = {
	name: string;
	code: string;
};

export interface answer {
	answer_text: string;
	answer_weight: number;
}

export interface question {
	question_name: string;
	question_text: string;
	question_type: string;
	points_possible: number;
	position: number;
	answers: answer[];
}

export async function createPracticeQuiz(instituteId: number, lms: Lms, lmsCourseId: string, origin: string) {
	const createdQuiz = (await apiClient.lmsInfo.createAssessment(lms, lmsCourseId)) as Record<string, string>;
	const extractHostname = (url: string): string => {
		if (!url.startsWith("http://") && !url.startsWith("https://")) {
			url = "https://" + url;
		}
		try {
			return new URL(url).hostname;
		} catch (error) {
			console.error("Invalid URL:", url);
			return "";
		}
	};
	// As 23-June-2023, BlackBoard API does not support creating non-presentation-type assignments(quiz) nor questions
	// TODO: when it is allowed, fix the create quiz API call in \backend\general-node\src\routers\assessment-router.ts
	let quizId =
		lms === "canvas"
			? createdQuiz.id
			: lms === "desire2learn"
				? createdQuiz.QuizId
				: lms === "BlackboardLearn"
					? createdQuiz.contentId
					: null;
	// When creating assignment, blackboard Ultra provide assessmentId as return value, but Original only provide contentId
	if (createdQuiz.assessmentId && lms === "BlackboardLearn") {
		quizId = createdQuiz.assessmentId;
	}
	if (quizId) {
		// Create Questions *IMPORTANT dont use promie.all will create 1 qiz but multiple assessments
		const question1 = await createQuestion(
			lms,
			lmsCourseId,
			quizId,
			extractHostname(origin),
			formatQuestion(
				i18n.t("portal.components.assessmentPage.assessmentsPageHelperTSX.question1Title"),
				i18n.t("portal.components.assessmentPage.assessmentsPageHelperTSX.question1Description"),
				"short_answer_question",
				0,
				1,
				[{ answer_text: "yes", answer_weight: 100 }],
			),
		);
		const question2 = await createQuestion(
			lms,
			lmsCourseId,
			quizId,
			extractHostname(origin),
			formatQuestion(
				i18n.t("portal.components.assessmentPage.assessmentsPageHelperTSX.question2Title"),
				i18n.t("portal.components.assessmentPage.assessmentsPageHelperTSX.question2Description"),
				"short_answer_question",
				0,
				2,
				[{ answer_text: "yes", answer_weight: 100 }],
			),
		);
		const question3 = await createQuestion(
			lms,
			lmsCourseId,
			quizId,
			extractHostname(origin),
			formatQuestion(
				i18n.t("portal.components.assessmentPage.assessmentsPageHelperTSX.question3Title"),
				"1 + 1 =",
				"calculated_question",
				0,
				3,
				[{ answer_text: "2", answer_weight: 100 }],
			),
		);
		if (question1 && question2 && question3) {
			// Create Yuja Options
			const accessCode = generateKey(instituteId, lmsCourseId, quizId);
			const hostname =
				lms === "canvas"
					? extractHostname(origin)
					: lms === "desire2learn"
						? new URL(origin)?.hostname
						: lms === "BlackboardLearn"
							? new URL(origin)?.hostname
							: "";
			await createDefaultVerityOptions(lms, extractHostname(hostname), lmsCourseId, quizId, accessCode);
			// Publish Quiz
			if (lms === "canvas" || lms === "desire2learn") {
				const published = await apiClient.lmsInfo.updateAssessment(lms, lmsCourseId, quizId, accessCode);
				if (published) return;
				else throw Error(i18n.t("portal.components.assessmentPage.assessmentsPageHelperTSX.quizPubliFail"));
			} else return;
		} else throw Error(i18n.t("portal.components.assessmentPage.assessmentsPageHelperTSX.quizCreaFail"));
	} else throw Error(i18n.t("portal.components.assessmentPage.assessmentsPageHelperTSX.quizCreaFail"));
}

export function formatQuestion(
	question_name: string,
	question_text: string,
	question_type: string,
	points_possible: number,
	position: number,
	answers: answer[],
): question {
	return {
		question_name: question_name,
		question_text: question_text,
		question_type: question_type,
		points_possible: points_possible,
		position: position,
		answers: answers,
	} as question;
}

export async function createQuestion(
	lms: Lms,
	lmsCourseId: string,
	quizId: string,
	origin: string,
	question: question,
): Promise<boolean> {
	return apiClient.lmsInfo.addQuestionToAssessment(lms, lmsCourseId, quizId, origin, question);
}

export function getRandomInt(max: number): number {
	// Create byte array and fill with 1 random number
	const byteArray = new Uint8Array(1);
	window.crypto.getRandomValues(byteArray);

	const range = max + 1;
	const max_range = 256;
	if (byteArray[0] >= Math.floor(max_range / range) * range) return getRandomInt(max);
	return byteArray[0] % range;
}

function generateKey(instituteId: number, courseId: string, quizId: string) {
	const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
	const randomString = new Array(12)
		.fill(null)
		.map((_) => chars.charAt(getRandomInt(chars.length - 1)))
		.join("");
	const key = `${instituteId}_${courseId}_${quizId}_${randomString}`;
	return `VP_${btoa(key)}`.replace(/=/g, "").slice(0, 20);
}

export async function createDefaultVerityOptions(
	lms: Lms,
	origin: string,
	lmsCourseId: string,
	quizId: string,
	password: string,
) {
	// Random Password from Extension Code
	const suffix = await apiClient.institute.getSuffix();
	const quizInfo = {
		lms: lms,
		hostname: origin,
		courseId: lmsCourseId,
		quizId: quizId,
		quizInfo: {
			name: "YuJa Verity Practice Assessment" + (suffix ? ` (${suffix})` : ""),
		},
	} satisfies AssessmentInfo;
	const verityOptions: Partial<VerityOptions> = {
		enabled: true,
		videoRecordingMode: "normal",
		audioRecordingMode: "normal",
		recordScreen: true,
		recordWebTraffic: true,
		detectSidePanel: true,
		oneScreenEnforcementMode: "normal",
		showRecordingPanel: true,
		disableNewTab: true,
		closeOpenTabs: true,
		tabWhiteList: [],
		disableClipboardMode: "tab",
		enableGuidedAccessMode: true,
		restrictHandoffMode: "normal",
		idVerification: true,
		isLiveProctored: false,
		isLivePopIn: false,
		isExtension: true,
		isLockdown: false,
		isMobile: false,
		password: password,
	};
	await apiClient.verityOptions.save({
		quizInfo,
		courseInfo: { code: "Proctoring" } as CourseInfo,
		options: verityOptions,
	});
}

export function isStandaloneAssessment(quiz: QuizWithProctoringOptions) {
	return quiz.quizInfo && Object.keys(quiz.quizInfo).includes("standaloneInfo");
}

export function isProctorModeDisabled(
	verityInstituteSettings: VerityInstituteSettings,
	proctoringOptions?: QuizWithProctoringOptions["proctoringOptions"],
) {
	return (
		proctoringOptions?.enabled &&
		!(
			(proctoringOptions?.isExtension && verityInstituteSettings.allowExtensionPlatform) ||
			(proctoringOptions?.isLockdown && verityInstituteSettings.allowLockdownPlatform) ||
			(proctoringOptions?.isMobile && verityInstituteSettings.allowMobilePlatform)
		)
	);
}
